import React, {useEffect, useState, useRef} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {Box, Stack, Typography} from '@mui/material';
import {AppBarV2} from './AppBar';
import NoAccessIcon from '../assets/no-access.svg';
import {useValidateShareLinkQuery} from '../data/documentWorkspaceApi';
import {
  setOrg,
  setShareLinkMode,
  setSharedDocumentOrg,
  clearSharedDocumentOrg,
  selectIsExternalToEffectiveOrg,
} from '../data/session';
import {GREY_70} from '../App';
import {
  getSharedDocumentData,
  setSharedDocumentData,
  clearSharedDocumentData,
} from '../util/sharedDocumentStorage';

/**
 * SharedDocumentValidator - Validates shared document access before allowing document access
 *
 * This component wraps children and validates shared=true from URL query params.
 * If validation fails, it prevents rendering and shows an error.
 * If validation succeeds or no shared=true is present, it renders children normally.
 */
const SharedDocumentValidator = ({children}) => {
  const {documentId} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {isAuthenticated, currentOrg, orgs, isExternalUser} = useSelector(
    (state) => state.session
  );
  const isExternalToEffectiveOrg = useSelector(selectIsExternalToEffectiveOrg);

  // Early check: if no shared=true in URL, skip all validation logic
  const hasSharedInUrl = location.search.includes('shared=true');
  const queryParams = hasSharedInUrl
    ? new URLSearchParams(location.search)
    : null;
  const isShared = queryParams?.get('shared') === 'true';
  const isDocumentRoute =
    location.pathname.includes('/documents/') && documentId != null;
  const [hasValidated, setHasValidated] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const prevPathnameRef = useRef(null);

  // Initialize shared document mode from localStorage on mount (e.g., after page refresh)
  useEffect(() => {
    const sharedDocumentData = getSharedDocumentData();
    if (
      sharedDocumentData?.documentId &&
      documentId === sharedDocumentData.documentId
    ) {
      dispatch(setShareLinkMode({isShareLinkMode: true}));
      // Restore sharedDocumentOrg from localStorage on refresh
      dispatch(
        setSharedDocumentOrg({
          org: {
            orgId: sharedDocumentData.orgId,
            orgName: sharedDocumentData.orgName,
            documentId: sharedDocumentData.documentId,
            permission: sharedDocumentData.permission,
          },
        })
      );
    } else if (!sharedDocumentData?.documentId) {
      dispatch(setShareLinkMode({isShareLinkMode: false}));
    }
  }, [documentId, dispatch]);

  // Only validate if: document route + shared=true present + authenticated + not already validated
  const shouldValidate =
    isDocumentRoute &&
    (isShared || isExternalUser || isExternalToEffectiveOrg) &&
    documentId != null &&
    isAuthenticated &&
    !hasValidated;

  const {
    data: shareLinkData,
    isError: shareLinkError,
    isSuccess: shareLinkSuccess,
  } = useValidateShareLinkQuery(
    {documentId},
    {
      skip: !shouldValidate,
    }
  );

  useEffect(() => {
    if (!shouldValidate) {
      return;
    }

    if (shareLinkError) {
      clearSharedDocumentData();
      dispatch(setShareLinkMode({isShareLinkMode: false}));
      dispatch(clearSharedDocumentOrg());
      setValidationError(true);
      setHasValidated(true);
      return;
    }

    if (shareLinkSuccess && shareLinkData) {
      const {
        isValid,
        orgId,
        orgName,
        grantedViaParentOrg,
        grantedViaEmail,
        permission,
      } = shareLinkData;

      if (!isValid) {
        clearSharedDocumentData();
        dispatch(setShareLinkMode({isShareLinkMode: false}));
        dispatch(clearSharedDocumentOrg());
        setValidationError(true);
        setHasValidated(true);
        return;
      }

      if (documentId && orgId) {
        setSharedDocumentData({
          documentId,
          orgId,
          orgName,
          permission,
          grantedViaParentOrg,
          grantedViaEmail,
        });
        dispatch(setShareLinkMode({isShareLinkMode: true}));
        dispatch(setSharedDocumentOrg({org: {orgId, orgName, documentId}}));
      }

      // Only try to switch orgs if:
      // 1. User is a direct member of the org (not granted via parent org fallback)
      // 2. The orgId differs from current org
      // 3. The user has that org in their orgs list
      if (
        !grantedViaParentOrg &&
        orgId &&
        orgId !== currentOrg?.orgId &&
        orgs.length > 0
      ) {
        const targetOrg = orgs.find((org) => org.orgId === orgId);

        if (targetOrg) {
          localStorage.setItem('selectedOrgId', orgId);
          dispatch(setOrg({org: targetOrg}));
          window.location.reload();
          return;
        }
      }

      setHasValidated(true);
      const newSearch = new URLSearchParams(location.search);
      newSearch.delete('shared');
      const newUrl = `${location.pathname}${newSearch.toString() ? `?${newSearch.toString()}` : ''}`;
      navigate(newUrl, {replace: true});
    }
  }, [
    shouldValidate,
    shareLinkError,
    shareLinkSuccess,
    shareLinkData,
    location,
    navigate,
    dispatch,
    currentOrg,
    orgs,
    documentId,
  ]);

  // Clean up shared document data when navigating away from the document
  useEffect(() => {
    const currentPathname = location.pathname;
    const prevPathname = prevPathnameRef.current;

    // Only track transitions after the first render (prevPathname !== null)
    // If we're leaving a document route (was on /documents/:documentId, now on something else), clear the shared document data
    if (
      prevPathname !== null &&
      prevPathname.startsWith('/documents/') &&
      !currentPathname.startsWith('/documents/')
    ) {
      clearSharedDocumentData();
      dispatch(setShareLinkMode({isShareLinkMode: false}));
      dispatch(clearSharedDocumentOrg());
    }

    prevPathnameRef.current = currentPathname;
  }, [location.pathname, dispatch]);

  // Cleanup on unmount: runs when component unmounts (e.g., navigating away from WorkspaceLayout routes)
  // Only clear if we're actually leaving a document route (check window.location since component is unmounting)
  useEffect(() => {
    return () => {
      // Check if we're still on a document route using window.location (available during unmount)
      const currentPath = window.location.pathname;
      const isStillOnDocumentRoute = currentPath.startsWith('/documents/');

      // Only clear if we're NOT still on a document route (navigating away)
      if (!isStillOnDocumentRoute) {
        const sharedDocumentData = getSharedDocumentData();
        if (sharedDocumentData?.documentId) {
          clearSharedDocumentData();
          dispatch(setShareLinkMode({isShareLinkMode: false}));
          dispatch(clearSharedDocumentOrg());
        }
      }
    };
  }, [dispatch]);

  if (validationError) {
    return (
      <Stack direction="column" height="100vh" width="100vw" bgcolor={GREY_70}>
        <AppBarV2 />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
          }}
        >
          <img src={NoAccessIcon} alt="No access" width={64} height={64} />
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
            }}
          >
            No access
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '150%',
              letterSpacing: '-0.14px',
              color: '#819190',
              textAlign: 'center',
            }}
          >
            You don&apos;t have access to this document. Please contact your
            engagement team.
          </Typography>
        </Box>
      </Stack>
    );
  }

  if (isShared && !hasValidated && isDocumentRoute) {
    return (
      <Stack
        direction="column"
        height="100vh"
        width="100vw"
        bgcolor={GREY_70}
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body1">
          Validating shared document access...
        </Typography>
      </Stack>
    );
  }

  return children;
};

SharedDocumentValidator.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SharedDocumentValidator;
